import React, { useContext, useState } from 'react';
import { useSubjectContext } from './index';
import {
  Button,
  FabButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@dls/react-core';
import { Collapse, TableContainer } from '@material-ui/core';
import {
  PageWrapper,
  StyledCollapsibleTableRow,
  StyledTableNoBorder,
} from '../../components/styled/styles';
import {
  EnhancedTableHead,
  HeadCell,
  Order,
  sortedTableStyles,
} from '../../components/sorted-table/sorted-table-head';
import { Devices, getSubjectDevices } from '../../api/studykit';
import { getComparator, stableSort } from '../../components/sorted-table/sorting-utils';
import { Icon } from '@dls/react-icon';
import { ColorDLS, ColorX11 } from '../../styling/color';
import { CreateSubjectDeviceDialog } from '../../components/dialogs/create-subject-device-dialog';
import { EditSubjectDeviceDialog } from '../../components/dialogs/edit-subject-device-dialog';
import { RemoveDeviceDialog } from '../../components/dialogs/remove-device-dialog';
import { StudyPageContext } from '../../App';
import useRequest from '../../api/useRequest';
import { KeyedMutator } from 'swr/dist/types';
import { HasPermission } from '../../context/authorization';

const headCells: HeadCell<Devices>[] = [
  {
    label: 'Device Type',
    type: 'sortable',
    numeric: false,
    disablePadding: true,
    id: 'type',
  },
  {
    label: 'Device Name',
    type: 'sortable',
    numeric: false,
    disablePadding: true,
    id: 'name',
  },
  {
    label: 'Position',
    type: 'sortable',
    numeric: false,
    disablePadding: true,
    id: 'bodyPosition',
  },
  {
    label: 'MAC Address',
    type: 'sortable',
    numeric: false,
    disablePadding: true,
    id: 'macAddress',
  },
  {
    label: 'Serial number',
    type: 'sortable',
    numeric: false,
    disablePadding: true,
    id: 'serial',
  },
  {
    label: 'Last Sync',
    type: 'sortable',
    numeric: false,
    disablePadding: true,
    id: 'lastSync',
  },
  {
    label: '',
    type: 'other',
    disablePadding: true,
    id: '',
  },
];

export const SubjectDevices = () => {
  const tableClasses = sortedTableStyles();
  const { subject } = useSubjectContext();
  const { study } = useContext(StudyPageContext);

  const [openAddDeviceDialog, setOpenAddDeviceDialog] = useState(false);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Devices>('type');

  const { data: devices, error, mutate } = useRequest(getSubjectDevices(study.id, subject.id));

  if (error) {
    return (
      <PageWrapper>
        <Typography variant={'h1'}>Something went wrong...</Typography>
      </PageWrapper>
    );
  }

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Devices) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <PageWrapper>
      <Paper className={'dlsTable'}>
        <CreateSubjectDeviceDialog
          mutate={mutate}
          open={openAddDeviceDialog}
          onClose={() => setOpenAddDeviceDialog(false)}
          subject={subject}
        />
        <div style={{ display: `flex`, justifyContent: `space-between`, alignContent: `center` }}>
          <Typography weight={'bold'} variant={'body1'} id="tableTitle">
            Device overview
          </Typography>
          <HasPermission value="Devices.Create">
            <Button
              onClick={() => setOpenAddDeviceDialog(true)}
              startIcon={<Icon name={'Plus'} color={ColorX11.White} size={'m'} />}
              style={{ width: `12em` }}
              dls_variant={'primary'}
            >
              Add new device
            </Button>
          </HasPermission>
        </div>
        {devices === undefined ? (
          <LinearProgress variant={'indeterminate'} />
        ) : (
          <TableContainer style={{ width: `unset` }}>
            <Table>
              <EnhancedTableHead
                classes={tableClasses}
                onRequestSort={handleRequestSort}
                order={order}
                orderBy={orderBy}
                headCells={headCells}
              />
              <TableBody className={'collapsible-table'}>
                {devices && devices.length > 0 ? (
                  stableSort(devices, getComparator(order, orderBy)).map((row) => {
                    return (
                      <SingleRow
                        mutate={mutate}
                        subjectId={subject.id}
                        key={row.serial || row.macAddress}
                        device={row}
                      />
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={headCells.length} style={{ textAlign: 'center' }}>
                      There are no devices assigned to this subjects yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </PageWrapper>
  );
};

interface RowProps {
  device: Devices;
  subjectId: string;
  mutate: KeyedMutator<any>;
}

const SingleRow: React.FC<RowProps> = ({ device, subjectId, mutate }) => {
  const [open, setOpen] = useState(false);

  return (
    <React.Fragment>
      <StyledCollapsibleTableRow className={`toggle-row`} data-testid={`row-${device.id}`}>
        <TableCell scope={'row'} data-testid={`device-type-${device.id}`}>
          {device.type}
        </TableCell>
        <TableCell scope={'row'} data-testid={`device-name-${device.id}`}>
          {device.name}
        </TableCell>
        <TableCell
          scope={'row'}
          style={{ textTransform: `capitalize` }}
          data-testid={`device-position-${device.id}`}
        >
          {device.bodyPosition}
        </TableCell>
        <TableCell scope={'row'} data-testid={`device-mac-${device.id}`}>
          {device.macAddress}
        </TableCell>
        <TableCell scope={'row'} data-testid={`device-serial-${device.id}`}>
          {device.serial || 'N/A'}
        </TableCell>
        <TableCell scope={'row'} data-testid={`device-sync-${device.id}`}>
          {device.lastSync ? device.lastSync : 'N/A'}
        </TableCell>
        <TableCell className={'toggle-cell'}>
          <FabButton
            size="small"
            variant="extended"
            dls_variant="quietDefault"
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <Icon name={'NavigationUp'} size={'m'} />
            ) : (
              <Icon name={'NavigationDown'} size={'m'} />
            )}
          </FabButton>
        </TableCell>
      </StyledCollapsibleTableRow>
      <TableRow
        className={'collapsible-row'}
        style={{ display: open ? '' : 'none', backgroundColor: ColorDLS.Blue05 }}
      >
        <TableCell style={{ padding: 0 }} className={'collapsible-cell'} colSpan={headCells.length}>
          <CollapsibleDetails
            key={device.id}
            open={open}
            device={device}
            subjectId={subjectId}
            mutate={mutate}
          />
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface CollapsibleDetailsProps {
  open: boolean;
  device: Devices;
  subjectId: string;
  mutate: KeyedMutator<any>;
}

const CollapsibleDetails: React.FC<CollapsibleDetailsProps> = ({
  open,
  device,
  subjectId,
  mutate,
}) => {
  const [openEditDeviceDialog, setOpenEditDeviceDialog] = useState(false);
  const [openRemoveDeviceDialog, setOpenRemoveDeviceDialog] = useState(false);
  return (
    <>
      <EditSubjectDeviceDialog
        open={openEditDeviceDialog}
        onClose={() => setOpenEditDeviceDialog(false)}
        device={device}
        subjectId={subjectId}
        mutate={mutate}
      />
      <RemoveDeviceDialog
        mutate={mutate}
        subjectId={subjectId}
        device={device}
        open={openRemoveDeviceDialog}
        onClose={() => setOpenRemoveDeviceDialog(false)}
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableNoBorder scope={'col'}>
                <Typography weight={'bold'}>Usage Start</Typography>
              </StyledTableNoBorder>
              <StyledTableNoBorder scope={'col'}>
                <Typography weight={'bold'}>Usage End</Typography>
              </StyledTableNoBorder>
              <StyledTableNoBorder scope={'col'}>
                <Typography weight={'bold'}>Firmware Version</Typography>
              </StyledTableNoBorder>
              <StyledTableNoBorder scope={'col'}>
                <Typography weight={'bold'}>Software Version</Typography>
              </StyledTableNoBorder>
              <StyledTableNoBorder scope={'col'} />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <StyledTableNoBorder scope={'row'}>
                {new Date(device.usageStart).toLocaleDateString()}
              </StyledTableNoBorder>
              <StyledTableNoBorder scope={'row'}>
                {new Date(device.usageEnd).toLocaleDateString()}
              </StyledTableNoBorder>
              <StyledTableNoBorder scope={'row'}>
                {device.hardwareVersion || 'N/A'}
              </StyledTableNoBorder>
              <StyledTableNoBorder scope={'row'}>
                {device.softwareVersion || 'N/A'}
              </StyledTableNoBorder>
              <StyledTableNoBorder style={{ textAlign: `end` }} scope={'row'}>
                <HasPermission value="Devices.Update">
                  <FabButton
                    onClick={() => setOpenEditDeviceDialog(true)}
                    size={'small'}
                    dls_variant={'quietEmphasis'}
                  >
                    <Icon name={'Edit'} size={'m'} color={ColorDLS.Blue45} />
                  </FabButton>
                </HasPermission>
                <HasPermission value="Devices.Delete">
                  <FabButton
                    style={{ marginLeft: `8px` }}
                    onClick={() => setOpenRemoveDeviceDialog(true)}
                    size={'small'}
                    dls_variant={'quietDefault'}
                  >
                    <Icon name={'DeleteTrash'} size={'m'} color={ColorDLS.Blue45} />
                  </FabButton>
                </HasPermission>
              </StyledTableNoBorder>
            </TableRow>
          </TableBody>
        </Table>
      </Collapse>
    </>
  );
};
